import {
    Button,
    Select,
    InputUnits,
    ColorPicker,
    ToolbarPopover,
    InputAuto,
    Link,
    Image
} from './renders';

/**
 * @typedef {import('./constants').NodeType} NodeType
 * @typedef {import('./constants').MarkType} MarkType
 * @typedef {import('./constants').CommandType} CommandType
 * @typedef {import('./constants').ToolType} ToolType
 * @typedef {import('./constants').WysiwygRender} WysiwygRender
 * @typedef {import('./constants').WysiwygAutofocus} WysiwygAutofocus
 * @typedef {import('./constants').ICommand} ICommand
 * @typedef {import('./constants').ITool} ITool
 */

/**
 * @type NodeType
 */
export const NodeType = Object.freeze({
    BLOCKQUOTE: 'blockquote',
    BULLET_LIST: 'bulletList',
    CODE_BLOCK: 'codeBlock',
    DOCUMENT: 'document',
    HARD_BREAK: 'hardBreak',
    HEADING: 'heading',
    HORIZONTAL_RULE: 'horizontalRule',
    IMAGE: 'image',
    LIST_ITEM: 'listItem',
    ORDERED_LIST: 'orderedList',
    PARAGRAPH: 'paragraph',
    TABLE: 'table',
    TABLE_ROW: 'tableRow',
    TABLE_CELL: 'tableCell',
    TEXT: 'text'
});

/**
 * @type MarkType
 */
export const MarkType = Object.freeze({
    BOLD: 'bold',
    CODE: 'code',
    ITALIC: 'italic',
    LINK: 'link',
    STRIKE: 'strike',
    UNDERLINE: 'underline',
    TEXT_STYLE: 'textStyle'
});

/**
 * @type CommandType
 */
export const CommandType = Object.freeze({
    /* paragraph style */
    PARAGRAPH: 'paragraph',
    HEADING_1: `heading1`,
    HEADING_2: `heading2`,
    HEADING_3: `heading3`,
    HEADING_4: `heading4`,
    TEXT_SMALL: 'textSmall',
    TEXT_XSMALL: 'textXsmall',
    CODE_BLOCK: 'codeBlock',
    /* list */
    SINK_LIST_ITEM: 'sinkListItem',
    LIFT_LIST_ITEM: 'liftListItem'
});

/**
 * @type ToolType
 */
export const ToolType = Object.freeze({
    /* typography */
    PARAGRAPH_STYLE: 'paragraphStyle',
    FONT_SIZE: 'fontSize',
    FONT_FAMILY: 'fontFamily',
    TEXT_COLOR: 'textColor',
    BLOCKQUOTE: 'blockquote',
    /* lists */
    ORDERED_LIST: 'orderedList',
    BULLET_LIST: 'bulletList',
    /* alignment */
    TEXT_ALIGN_LEFT: 'textAlignLeft',
    TEXT_ALIGN_RIGHT: 'textAlignRight',
    TEXT_ALIGN_CENTER: 'textAlignCenter',
    /* insertion */
    LINK: 'link',
    IMAGE: 'image',
    TABLE: 'table',
    /* formatting */
    BOLD: 'bold',
    CODE: 'code',
    ITALIC: 'italic',
    STRIKE: 'strike',
    UNDERLINE: 'underline',
    /* history */
    UNDO: 'undo',
    REDO: 'redo',
    SAVE: 'save',
    /* other */
    CLEAR_FORMATTING: 'clearFormatting',
    HARD_BREAK: 'hardBreak',
    HORIZONTAL_RULE: 'horizontalRule',
    /** table options  */
    INSERT_TABLE: 'insertTable',
    DELETE_TABLE: 'deleteTable',
    ADD_COLUMN_BEFORE: 'addColumnBefore',
    ADD_COLUMN_AFTER: 'addColumnAfter',
    DELETE_COLUMN: 'deleteColumn',
    ADD_ROW_BEFORE: 'addRowBefore',
    ADD_ROW_AFTER: 'addRowAfter',
    DELETE_ROW: 'deleteRow',
    MERGE_CELLS: 'mergeCells',
    SPLIT_CELL: 'splitCell',
    TOGGLE_HEADER_COLUMN: 'toggleHeaderColumn',
    TOGGLE_HEADER_ROW: 'toggleHeaderRow',
    ALIGN_V_TOP: 'alignVTop',
    ALIGN_V_MID: 'alignVMid',
    ALIGN_V_BOT: 'alignVBot',
    TOGGLE_BORDERS: 'toggleBorders',
    TOGGLE_ZEBRA: 'toggleZebra',
    /** image options */
    INSERT_IMAGE: 'insertImage'
});

/**
 * @param {Partial<ICommand>} command
 * @return {ICommand}
 */
export const createCommand = ({
    name = '',
    title = 'Unknown command',
    exec = () => {},
    isActive = () => false,
    isEnabled = () => true
}) => ({
    name,
    title,
    exec,
    isActive,
    isEnabled
});

/**
 * @type WysiwygRender
 */
export const WysiwygRender = Object.freeze({
    BUTTON: Button,
    COLOR_PICKER: ColorPicker,
    INPUT_UNITS: InputUnits,
    SELECT: Select,
    TOOLBAR_POPOVER: ToolbarPopover,
    INPUT_AUTO: InputAuto,
    LINK: Link,
    IMAGE: Image
});

/**
 * @param {Partial<ITool>} tool
 * @return {ITool}
 */
export const createTool = ({
    name = '',
    render = WysiwygRender.BUTTON,
    title = 'Unknown tool',
    icon = 'help-circle-outline',
    exec = () => {},
    isActive = () => false,
    isEnabled = () => true,
    ...optionalRest
}) => ({
    name,
    render,
    title,
    icon,
    exec,
    isActive,
    isEnabled,
    ...optionalRest
});

/** @type WysiwygAutofocus */
export const WysiwygAutofocus = Object.freeze({
    START: 'start',
    END: 'end',
    ALL: 'all',
    ENABLED: true,
    DISABLED: false
});

export const EMPTY_PTAG_REGEXP = /^<p\s*([^>]*)\s*>(\s*)<\/p\s*>$/;

export const DefaultTools = [
    {
        title: 'Оформление',
        group: [ToolType.PARAGRAPH_STYLE, ToolType.TEXT_COLOR, ToolType.FONT_SIZE, ToolType.FONT_FAMILY, ToolType.BLOCKQUOTE]
    },
    {
        title: 'Форматирование',
        group: [ToolType.BOLD, ToolType.ITALIC, ToolType.UNDERLINE, ToolType.STRIKE, ToolType.CODE]
    },
    {
        title: 'Выравнивание',
        group: [ToolType.TEXT_ALIGN_LEFT, ToolType.TEXT_ALIGN_CENTER, ToolType.TEXT_ALIGN_RIGHT]
    },
    {
        title: 'Нумерация',
        group: [ToolType.ORDERED_LIST, ToolType.BULLET_LIST]
    },
    {
        title: 'Вставка',
        group: [ToolType.IMAGE, ToolType.LINK, ToolType.TABLE]
    },
    {
        title: '',
        group: [ToolType.CLEAR_FORMATTING, ToolType.HARD_BREAK]
    }
];
